/**
 * Wave 3.97 — CAD Weight Correction Toast helpers.
 *
 * Shared logic that turns the backend's ``_gold_shortfall`` payload
 * (returned from POST /orders/{id}/milestones/cad/tick) into a Sonner
 * toast with a one-tap "Top-up" or "Reduce" action. The reconcile
 * endpoint (POST /orders/{id}/gold/top-up-from-cad) does the actual
 * requirement adjustment server-side.
 *
 * Also exports ``computeGoldShortfallClient(order)`` — a cheap
 * client-side re-computation of the same shortfall so the persistent
 * badge on the Gold Requirements section stays in sync between
 * server-driven and locally-driven UI states.
 */
import { toast } from 'sonner';
import { api } from './api';
import { extractError } from './errors';
import { formatGrams } from './format';

const EPSILON = 0.001;

/**
 * Format a signed delta as ``+X g`` / ``−X g``. Uses ``formatGrams`` so
 * it matches the display precision used across the app.
 */
export function formatDeltaGrams(delta) {
  const sign = delta > 0 ? '+' : (delta < 0 ? '−' : '');
  const magnitude = formatGrams(Math.abs(Number(delta) || 0));
  return `${sign}${magnitude} g`;
}

/**
 * Pure client-side recompute of ``_gold_shortfall`` for a given order.
 * Kept structurally identical to the backend helper so the badge
 * disappears without a round-trip once the requirement is reconciled.
 */
export function computeGoldShortfallClient(order) {
  if (!order || typeof order !== 'object') return null;
  const cadWt = Number(order.cad_weight_grams || 0);
  const reqs = Array.isArray(order.requirements) ? order.requirements : [];
  const goldReqs = reqs.filter((r) => (r?.material_type || '') === 'gold');

  let totalRequired = 0;
  let totalFulfilled = 0;
  for (const r of goldReqs) {
    totalRequired += Number(r?.required_grams || 0);
    totalFulfilled += Number(r?.fulfilled_grams || 0);
  }
  const delta = Math.round((cadWt - totalRequired) * 1000) / 1000;

  const purityId = order?.jewellery_purity_id || null;
  const purityLabel = order?.jewellery_purity_label || order?.jewellery_purity_name || null;
  const purityPercent = Number(order?.jewellery_purity_percent || 0);

  const isPending = (r) => {
    const status = String(r?.status || 'open').toLowerCase();
    if (status === 'complete') return false;
    const req = Number(r?.required_grams || 0);
    const fil = Number(r?.fulfilled_grams || 0);
    return req > fil + 1e-6;
  };
  const matchesPurity = (r) => {
    if (purityId && r?.classification_id === purityId) return true;
    if (purityPercent) {
      const rp = Number(r?.required_purity || 0);
      if (rp && Math.abs(rp - purityPercent) < 0.01) return true;
    }
    return false;
  };

  const pendingMatching = goldReqs.filter((r) => isPending(r) && matchesPurity(r));
  const pendingAny = goldReqs.filter(isPending);
  const target = pendingMatching[pendingMatching.length - 1]
    || pendingAny[pendingAny.length - 1]
    || null;

  let actionSuggested = null;
  const hasGold = goldReqs.length > 0;
  if (cadWt > 0 && hasGold && Math.abs(delta) >= EPSILON) {
    if (delta > 0) actionSuggested = 'top_up';
    else if (delta < 0 && goldReqs.some(isPending)) actionSuggested = 'reduce';
  }

  return {
    has_gold_requirements: hasGold,
    cad_weight_grams: cadWt,
    gold_required_total_grams: totalRequired,
    gold_fulfilled_total_grams: totalFulfilled,
    delta_grams: delta,
    action_suggested: actionSuggested,
    purity_id: purityId,
    purity_label: purityLabel,
    target_req_id: target?.id || null,
    target_req_pending_grams: target
      ? Math.max(0, Number(target.required_grams || 0) - Number(target.fulfilled_grams || 0))
      : null,
    target_req_required_grams: target ? Number(target.required_grams || 0) : null,
    target_req_fulfilled_grams: target ? Number(target.fulfilled_grams || 0) : null,
  };
}

/**
 * Call the reconcile endpoint and refresh UI. Shared between the toast
 * action button and the persistent badge's tap-to-reconcile button.
 */
export async function reconcileGoldFromCad(orderId, action, reload) {
  const busyToast = toast.loading(
    action === 'top_up'
      ? 'Topping up the gold requirement…'
      : 'Reducing the gold requirement…',
    { duration: 15000 },
  );
  try {
    const res = await api.post(`/orders/${orderId}/gold/top-up-from-cad`, { action });
    toast.dismiss(busyToast);
    const info = res?.data?._reconcile || {};
    const applied = Number(info.applied_grams || 0);
    const magnitude = formatGrams(Math.abs(applied));
    if (action === 'top_up') {
      toast.success(
        info.created_new_requirement
          ? `Added a new Gold requirement of ${magnitude} g`
          : `Topped up Gold requirement by ${magnitude} g`,
      );
    } else {
      toast.success(
        info.capped
          ? `Reduced Gold requirement by ${magnitude} g (capped so issued gold is preserved)`
          : `Reduced Gold requirement by ${magnitude} g`,
      );
    }
    if (typeof reload === 'function') {
      try { await reload(); } catch { /* ignore */ }
    }
    return res?.data || null;
  } catch (e) {
    toast.dismiss(busyToast);
    toast.error(extractError(e, 'Failed to reconcile gold requirement'));
    return null;
  }
}

/**
 * If the given shortfall payload suggests a reconcile action, fire the
 * Sonner toast with a one-tap action button. No-op otherwise. Safe to
 * call unconditionally after any CAD-weight tick / edit.
 *
 * Toast placement (per Wave 3.97 UX brief):
 *   - top_up  → warning-style neutral toast, persistent (Infinity)
 *   - reduce  → info toast, persistent (Infinity), optional action
 */
export function showGoldShortfallToast(shortfall, orderId, reload) {
  if (!shortfall || !shortfall.has_gold_requirements) return;
  const action = shortfall.action_suggested;
  if (!action) return;
  if (!orderId) return;
  const delta = Number(shortfall.delta_grams || 0);
  if (!(Math.abs(delta) >= EPSILON)) return;

  const purity = shortfall.purity_label || 'purity';
  const cadG = formatGrams(Number(shortfall.cad_weight_grams || 0));
  const reqG = formatGrams(Number(shortfall.gold_required_total_grams || 0));

  if (action === 'top_up') {
    const shortG = formatGrams(delta);
    toast.warning(
      `Gold shortfall detected — CAD is higher than the current gold requirement`,
      {
        description:
          `CAD ${cadG} g vs Gold required ${reqG} g (@ ${purity}). ` +
          `Top-up needed: +${shortG} g.`,
        duration: 15000,
        action: {
          label: `Top-up +${shortG} g`,
          onClick: () => reconcileGoldFromCad(orderId, 'top_up', reload),
        },
        // Sonner test-id hook — helps automation target this toast.
        id: `gold-shortfall-${orderId}`,
      },
    );
  } else if (action === 'reduce') {
    const surplusG = formatGrams(Math.abs(delta));
    toast.info(
      `Gold surplus detected — CAD is lower than the current gold requirement`,
      {
        description:
          `CAD ${cadG} g vs Gold required ${reqG} g (@ ${purity}). ` +
          `You may reduce the pending requirement by −${surplusG} g.`,
        duration: 15000,
        action: {
          label: `Reduce −${surplusG} g`,
          onClick: () => reconcileGoldFromCad(orderId, 'reduce', reload),
        },
        id: `gold-shortfall-${orderId}`,
      },
    );
  }
}
